import React, { useState } from 'react';
import { PaymentGatewayId } from '../../types';
import { useSettings } from '../SettingsContext';
import { isGatewayConnected } from '../../utils/gatewayUtils';

const GATEWAY_LABELS: Record<PaymentGatewayId, { label: string; description: string }> = {
  stripe: { label: 'Stripe', description: 'Global card processing, Apple Pay, Google Pay & 3DS' },
  razorpay: { label: 'Razorpay', description: 'UPI, cards, netbanking & automated Indian subscriptions' },
  cashfree: { label: 'Cashfree', description: 'Indian recurring payments, UPI AutoPay & netbanking' },
  lemonsqueezy: { label: 'Lemon Squeezy', description: 'Merchant of record, global sales tax & digital SaaS billing' },
  paddle: { label: 'Paddle', description: 'Global software Merchant of Record, EU VAT & PayPal' },
  checkoutcom: { label: 'Checkout.com', description: 'Enterprise global acquiring & multi-currency billing' },
  chargebee: { label: 'Chargebee', description: 'Subscription billing engine & complex tier management' },
};

const GatewayCredentialsForm: React.FC<{ onSave: () => void }> = ({ onSave }) => {
  const { settings, updateSettings } = useSettings();
  const [activeGatewayTab, setActiveGatewayTab] = useState<PaymentGatewayId>('stripe');
  const [savedSuccess, setSavedSuccess] = useState<string | null>(null);

  const updateCredentials = (gateway: PaymentGatewayId, data: Record<string, any>) => {
    const updated = {
      ...settings.gatewayCredentials,
      [gateway]: { ...(settings.gatewayCredentials?.[gateway] || {}), ...data },
    };
    updateSettings({ gatewayCredentials: updated });
  };

  const handleSave = async (gateway: PaymentGatewayId) => {
    try {
      await fetch('/api/v1/gateway-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gateway, credentials: settings.gatewayCredentials[gateway] }),
      }).catch(() => {});
      
      setSavedSuccess(gateway);
      setTimeout(() => setSavedSuccess(null), 3000);
      onSave();
    } catch (error) {
      console.error('Failed to save credentials:', error);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col md:flex-row min-h-[520px]">
      <div className="w-full md:w-72 border-r border-slate-100 bg-slate-50/50 p-6 space-y-1.5 shrink-0">
        <div className="px-3 pb-3 mb-2 border-b border-slate-200/60">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment Providers</p>
        </div>
        {Object.entries(GATEWAY_LABELS).map(([id, info]) => {
          const isConnected = isGatewayConnected(id as PaymentGatewayId, settings.gatewayCredentials);
          const isSelected = activeGatewayTab === id;

          return (
            <button
              key={id}
              onClick={() => setActiveGatewayTab(id as PaymentGatewayId)}
              className={`w-full text-left px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-between group ${
                isSelected ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <span>{info.label}</span>
              </div>
              {isConnected ? (
                <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full text-[9px] font-black">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span>LIVE</span>
                </div>
              ) : (
                <div className="w-1.5 h-1.5 bg-slate-300 rounded-full"></div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 p-8 md:p-10 space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-xl font-black text-slate-900">{GATEWAY_LABELS[activeGatewayTab].label} Integration</h3>
            <p className="text-xs text-slate-500 mt-0.5">{GATEWAY_LABELS[activeGatewayTab].description}</p>
          </div>
          {isGatewayConnected(activeGatewayTab, settings.gatewayCredentials) ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Connected &amp; Visible to Customers
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
              Hidden from Customers (Keys Required)
            </span>
          )}
        </div>

        {savedSuccess && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-xs font-bold flex items-center gap-2 animate-in fade-in">
            <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <span>Credentials saved successfully! Gateways with valid API keys will now appear during customer checkout.</span>
          </div>
        )}

        {/* STRIPE */}
        {activeGatewayTab === 'stripe' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Publishable Key</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="pk_live_..."
                value={settings.gatewayCredentials?.stripe?.publishableKey || ''}
                onChange={(e) => updateCredentials('stripe', { publishableKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Secret Key</label>
              <input
                type="password"
                title="Stripe Secret Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="sk_live_..."
                value={settings.gatewayCredentials?.stripe?.secretKey || ''}
                onChange={(e) => updateCredentials('stripe', { secretKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Webhook Signing Secret</label>
              <input
                type="password"
                title="Stripe Webhook Secret"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="whsec_..."
                value={settings.gatewayCredentials?.stripe?.webhookSecret || ''}
                onChange={(e) => updateCredentials('stripe', { webhookSecret: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('stripe')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Stripe Credentials
            </button>
          </div>
        )}

        {/* RAZORPAY */}
        {activeGatewayTab === 'razorpay' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Key ID</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="rzp_live_..."
                value={settings.gatewayCredentials?.razorpay?.keyId || ''}
                onChange={(e) => updateCredentials('razorpay', { keyId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Key Secret</label>
              <input
                type="password"
                title="Razorpay Key Secret"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="SECRET"
                value={settings.gatewayCredentials?.razorpay?.keySecret || ''}
                onChange={(e) => updateCredentials('razorpay', { keySecret: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Webhook Secret</label>
              <input
                type="password"
                title="Razorpay Webhook Secret"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="whsec_..."
                value={settings.gatewayCredentials?.razorpay?.webhookSecret || ''}
                onChange={(e) => updateCredentials('razorpay', { webhookSecret: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('razorpay')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Razorpay Credentials
            </button>
          </div>
        )}

        {/* CASHFREE */}
        {activeGatewayTab === 'cashfree' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">App ID</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="CF..."
                value={settings.gatewayCredentials?.cashfree?.appId || ''}
                onChange={(e) => updateCredentials('cashfree', { appId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Secret Key</label>
              <input
                type="password"
                title="Cashfree Secret Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="SECRET"
                value={settings.gatewayCredentials?.cashfree?.secretKey || ''}
                onChange={(e) => updateCredentials('cashfree', { secretKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Environment</label>
              <div className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl font-mono text-sm font-bold text-emerald-600 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                Production (Live Mode)
              </div>
            </div>
            <button
              onClick={() => handleSave('cashfree')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Cashfree Credentials
            </button>
          </div>
        )}

        {/* LEMON SQUEEZY */}
        {activeGatewayTab === 'lemonsqueezy' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">API Key</label>
              <input
                type="password"
                title="Lemon Squeezy API Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="eyJhbGciOi..."
                value={settings.gatewayCredentials?.lemonsqueezy?.apiKey || ''}
                onChange={(e) => updateCredentials('lemonsqueezy', { apiKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Webhook Signing Secret</label>
              <input
                type="password"
                title="Lemon Squeezy Webhook Secret"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="SECRET"
                value={settings.gatewayCredentials?.lemonsqueezy?.webhookSecret || ''}
                onChange={(e) => updateCredentials('lemonsqueezy', { webhookSecret: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('lemonsqueezy')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Lemon Squeezy Credentials
            </button>
          </div>
        )}

        {/* PADDLE */}
        {activeGatewayTab === 'paddle' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Vendor ID</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="12345"
                value={settings.gatewayCredentials?.paddle?.vendorId || ''}
                onChange={(e) => updateCredentials('paddle', { vendorId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Auth Code / API Key</label>
              <input
                type="password"
                title="Paddle Auth Code"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="SECRET"
                value={settings.gatewayCredentials?.paddle?.authCode || ''}
                onChange={(e) => updateCredentials('paddle', { authCode: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Public Key</label>
              <textarea
                title="Paddle Public Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm h-28"
                placeholder="-----BEGIN PUBLIC KEY-----..."
                value={settings.gatewayCredentials?.paddle?.publicKey || ''}
                onChange={(e) => updateCredentials('paddle', { publicKey: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('paddle')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Paddle Credentials
            </button>
          </div>
        )}

        {/* CHECKOUT.COM */}
        {activeGatewayTab === 'checkoutcom' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Public Key</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="pk_..."
                value={settings.gatewayCredentials?.checkoutcom?.publicKey || ''}
                onChange={(e) => updateCredentials('checkoutcom', { publicKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Secret Key</label>
              <input
                type="password"
                title="Checkout.com Secret Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="sk_..."
                value={settings.gatewayCredentials?.checkoutcom?.secretKey || ''}
                onChange={(e) => updateCredentials('checkoutcom', { secretKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Processing Channel ID</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="pc_..."
                value={settings.gatewayCredentials?.checkoutcom?.channelId || ''}
                onChange={(e) => updateCredentials('checkoutcom', { channelId: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('checkoutcom')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Checkout.com Credentials
            </button>
          </div>
        )}

        {/* CHARGEBEE */}
        {activeGatewayTab === 'chargebee' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Site Name</label>
              <input
                type="text"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="my-company-test"
                value={settings.gatewayCredentials?.chargebee?.site || ''}
                onChange={(e) => updateCredentials('chargebee', { site: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">API Key / Publishable Key</label>
              <input
                type="password"
                title="Chargebee API Key"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder="live_..."
                value={settings.gatewayCredentials?.chargebee?.publishableKey || ''}
                onChange={(e) => updateCredentials('chargebee', { publishableKey: e.target.value })}
              />
            </div>
            <button
              onClick={() => handleSave('chargebee')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
            >
              Save Chargebee Credentials
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GatewayCredentialsForm;
